import { useEffect, useState } from "react";
import { CalendarClock, Download, Gift, Loader2, MapPin, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CADENCE = { monthly: "Mensuel", bimonthly: "Bimestriel" };
const STATUS_OPTIONS = [
  { id: "a_preparer", label: "À préparer" },
  { id: "preparee", label: "Préparée" },
  { id: "expediee", label: "Expédiée" },
];
const STATUS_STYLES = {
  a_preparer: "bg-sage-light text-sage",
  preparee: "bg-amber-100 text-amber-800",
  expediee: "bg-sage text-white",
};

function formatDate(iso) {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function AdminSubscriptions() {
  const [rows, setRows] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [month, setMonth] = useState("all");

  useEffect(() => {
    api.get("/admin/subscription-deliveries").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);

  useEffect(() => {
    if (month !== "all" && rows && !rows.some((r) => (showPast || !r.past) && r.date.slice(0, 7) === month)) {
      setMonth("all");
    }
  }, [showPast, rows, month]);

  const monthKey = (iso) => (iso || "").slice(0, 7);
  const monthLabel = (key) => {
    try {
      return new Date(`${key}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    } catch {
      return key;
    }
  };
  const availableMonths = rows ? [...new Set(rows.filter((r) => showPast || !r.past).map((r) => monthKey(r.date)))].sort() : [];

  const visible = rows
    ? rows.filter((r) => (showPast || !r.past) && (month === "all" || monthKey(r.date) === month))
    : [];

  const setStatus = async (row, status) => {
    setRows((prev) => prev.map((r) => (r.session_id === row.session_id && r.delivery_key === row.delivery_key ? { ...r, status } : r)));
    try {
      await api.put("/admin/subscription-deliveries/status", { session_id: row.session_id, delivery_key: row.delivery_key, status });
    } catch {
      toast.error("Mise à jour du statut impossible");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/subscription-deliveries/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `livraisons_abonnements_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export impossible");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="admin-subscriptions" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-bark">Livraisons abonnements</h1>
          <p className="mt-1 text-sm text-bark-soft">Les prochaines box à préparer, calculées depuis chaque abonnement payé.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            data-testid="sub-month-filter"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-full border border-amber-900/20 bg-white px-4 py-2 text-sm font-medium text-bark outline-none focus:border-amber-deep"
          >
            <option value="all">Tous les mois</option>
            {availableMonths.map((m) => (
              <option key={m} value={m}>{monthLabel(m)}</option>
            ))}
          </select>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-bark-soft">
            <input data-testid="sub-show-past" type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} className="h-4 w-4 accent-amber-deep" />
            Livraisons passées
          </label>
          <button
            data-testid="sub-export-button"
            onClick={handleExport}
            disabled={exporting || !rows || rows.length === 0}
            className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-50"
          >
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Exporter (Excel)
          </button>
        </div>
      </div>

      {rows === null ? (
        <div className="flex items-center justify-center gap-2 py-16 text-bark-soft">
          <Loader2 className="animate-spin text-amber-deep" size={22} /> Chargement…
        </div>
      ) : visible.length === 0 ? (
        <div data-testid="subscriptions-empty" className="rounded-2xl border border-dashed border-amber-900/25 bg-white/60 py-16 text-center">
          <CalendarClock size={30} className="mx-auto mb-3 text-stone-mute" />
          <p className="text-sm text-stone-mute">Aucune livraison d'abonnement à venir pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => (
            <div key={`${r.session_id}-${r.delivery_key}`} data-testid={`sub-delivery-${r.session_id}-${r.delivery_index}`} className={`flex flex-wrap items-center gap-4 rounded-2xl border bg-white p-5 shadow-sm ${r.status === "expediee" ? "border-sage/30" : r.past ? "border-amber-900/10" : "border-amber-800/15"}`}>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-deep">
                <PackageCheck size={22} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-serif text-lg font-semibold text-bark">{formatDate(r.date)}</p>
                <p className="text-sm text-bark-soft">
                  {r.product_name} · {CADENCE[r.cadence] || r.cadence} · box {r.delivery_index}/{r.deliveries_total}
                </p>
                <p className="mt-0.5 flex items-center gap-1.5 text-sm text-bark">
                  {r.is_gift && <Gift size={13} className="text-terra" />}
                  <strong>{r.recipient}</strong>
                </p>
                {r.address && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-stone-mute">
                    <MapPin size={12} /> {r.address}
                  </p>
                )}
                {r.delivery_index === 1 && r.first_box && (
                  <p className="mt-0.5 text-xs font-medium text-amber-deep">{r.first_box}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[r.status] || "bg-sand text-stone-mute"}`}>
                  {STATUS_OPTIONS.find((s) => s.id === r.status)?.label || "À préparer"}
                </span>
                <select
                  data-testid={`sub-status-${r.session_id}-${r.delivery_index}`}
                  value={r.status}
                  onChange={(e) => setStatus(r, e.target.value)}
                  className="rounded-full border border-amber-900/20 bg-cream px-3 py-1.5 text-xs font-medium text-bark outline-none focus:border-amber-deep"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
